import React, { useEffect, useState } from "react";
import { Button, Modal, ReduxControls, ProgressIndicator } from "rhombus-react";
import { reduxForm, Field, initialize } from "redux-form";
import { useDispatch, useSelector } from "react-redux";

import { requiredNotNull } from "forms/validators/validators";
import { validVersionNameCharacters, versionNameMaxLength, versionNameExists } from "forms/validators/Versions";
import { fetchSingleVersion, updateVersion } from "reducers/CreativeVersions";

import { VERSION_RENAME_FORM } from "widgets/forms/list";
import { noop } from "utils/common";
import useFetchFallback from "hooks/useFetchFallback";

import "./RenameVersionModal.css";

export function RenameVersionModal({
	libraryId,
	creativeId,
	versionId,

	onClose = noop,

	asyncValidating,
	valid,
	handleSubmit,
	...props
}){
	const dispatch = useDispatch();
	const isVersionsLoaded = useSelector(state => state.creativeVersions.isLoaded);

	const [isSaving, setIsSaving] = useState(false);

	let versionIdInt = parseInt(versionId);

	/*
		We don't have an API endpoint for just renaming, so we need the version data
		so we can modify its name and then save the edit.
	*/
	const version = useFetchFallback({
		selector: state => state.creativeVersions.items.find(item => item.id === versionIdInt),
		fetcher: () => dispatch(fetchSingleVersion(libraryId, creativeId, versionId)),
		shouldFetch: isVersionsLoaded,
	});

	useEffect(() => {
		// Since we don't necessarily have the version in the store, we can't mapStateToProps the initial value
		let versionName = version?.name;
		if(versionName){
			dispatch(initialize(VERSION_RENAME_FORM, { name: versionName }));
		}
	}, [version]);

	function renameVersion(values){
		const { name } = values || {};
		let renamedVersion = { ...version, name };
		setIsSaving(true);

		dispatch(updateVersion(libraryId, creativeId, versionId, renamedVersion))
			.then((response) => {
				setIsSaving(false);
				if(response?.success){
					onClose();
				}
			});
	}

	function Body(){
		return (
			<div className="body-container">
				<form id={VERSION_RENAME_FORM} onSubmit={handleSubmit(renameVersion)}>
					<ProgressIndicator isLoading={!version || isSaving || asyncValidating} />
					<Field
						name = "name"
						label = "New Name for Version"
						component={ReduxControls.Input}
						validate = {[validVersionNameCharacters, versionNameMaxLength, requiredNotNull]}
						autoFocus
					/>
				</form>
			</div>
		);
	}

	function Footer(){
		return (
			<>
				<Button theme="default" disabled={!!asyncValidating || isSaving} onClick={onClose}>Cancel</Button>
				<Button
					type = "submit"
					form = {VERSION_RENAME_FORM}
					theme = "green"
					disabled = {!valid || !!asyncValidating || isSaving} // Button wants a pure bool, and asyncValidating will sometimes be a string
				>
					Save Name
				</Button>
			</>
		);
	}

	const modalProps = {
		title: "Rename Version",
		type: "modal",
		className: "version-rename-modal",
		xClose: true,
		allowFullScreen: false,
		setAppTitle: false,
		bladeProps: {
			verticallyCenter: true,
		},

		onClose,
		renderContent: Body,
		renderFooter: Footer,
	};

	return <Modal {...modalProps} />;
}

RenameVersionModal.displayName = "RenameVersionModal";

function asyncValidate(values, dispatch, props){
	let { name } = values;
	let { libraryId, creativeId } = props;

	return versionNameExists(dispatch, libraryId, creativeId, name, "name");
}

export default reduxForm({
	form: VERSION_RENAME_FORM,
	asyncValidate,
	asyncBlurFields: [], // This makes it just asyncValidate on submit
})(RenameVersionModal);
